import {
    currentPlatform,
    isPrimaryModifier,
} from "@design-system/Shortcut/formatShortcut";

export type GridDirection = "up" | "down" | "left" | "right";

export type GridKeyAction =
    | { type: "move"; direction: GridDirection }
    | { type: "extend"; direction: GridDirection }
    | { type: "rowStart" }
    | { type: "rowEnd" }
    | { type: "gridStart" }
    | { type: "gridEnd" }
    | { type: "pageUp" }
    | { type: "pageDown" }
    | { type: "open" }
    | { type: "toggleRowSelection" }
    | { type: "selectAll" }
    | { type: "selectRangeRows" }
    | { type: "selectRangeColumns" }
    | { type: "focusSearch" }
    | { type: "escape" };

/**
 * A key press with the platform's primary modifier folded into `isMod`, and
 * the other one (Ctrl on macOS, Meta elsewhere) in `hasOtherModifier`.
 * `isCtrl` is the raw Ctrl key, for Excel's Ctrl+Space on every platform.
 */
export interface NormalizedKey {
    key: string;
    isMod: boolean;
    isShift: boolean;
    isAlt: boolean;
    hasOtherModifier: boolean;
    isCtrl: boolean;
}

export const IS_APPLE_PLATFORM = currentPlatform === "mac";

const KEY_ALIASES: Record<string, string> = {
    " ": "space",
    esc: "escape",
    left: "arrowleft",
    right: "arrowright",
    up: "arrowup",
    down: "arrowdown",
    "←": "arrowleft",
    "→": "arrowright",
    "↑": "arrowup",
    "↓": "arrowdown",
};

const toKeyName = (key: string) => {
    const lower = key.toLowerCase();
    return KEY_ALIASES[lower] ?? lower;
};

export const normalizeKey = (
    event: Pick<
        KeyboardEvent,
        "metaKey" | "ctrlKey" | "shiftKey" | "altKey"
    > & {
        /** Chrome autofill sends keydown events without one. */
        key: string | undefined;
    },
): NormalizedKey => ({
    key: toKeyName(event.key ?? ""),
    isMod: isPrimaryModifier(event),
    isShift: event.shiftKey,
    isAlt: event.altKey,
    hasOtherModifier: IS_APPLE_PLATFORM ? event.ctrlKey : event.metaKey,
    isCtrl: event.ctrlKey,
});

const ARROW_DIRECTIONS: Record<string, GridDirection> = {
    arrowup: "up",
    arrowdown: "down",
    arrowleft: "left",
    arrowright: "right",
};

/**
 * Maps a key press inside the grid body to a grid action, or `null`.
 * Ctrl+Space selects the range's columns everywhere, since macOS keeps
 * Cmd+Space for Spotlight.
 */
export const resolveGridKey = ({
    key,
    isMod,
    isShift,
    isAlt,
    hasOtherModifier,
    isCtrl,
}: NormalizedKey): GridKeyAction | null => {
    const isCtrlOnly =
        isCtrl && !isShift && !isAlt && !(isMod && hasOtherModifier);
    if (key === "space" && isCtrlOnly) {
        return { type: "selectRangeColumns" };
    }
    if (isAlt || hasOtherModifier) {
        return null;
    }
    const direction = ARROW_DIRECTIONS[key];
    if (direction && !isMod) {
        return isShift
            ? { type: "extend", direction }
            : { type: "move", direction };
    }
    if (isShift) {
        if (isMod) {
            return null;
        }
        if (key === "space") {
            return { type: "selectRangeRows" };
        }
        return key === "/" ? { type: "focusSearch" } : null;
    }
    switch (key) {
        case "home":
            return isMod ? { type: "gridStart" } : { type: "rowStart" };
        case "end":
            return isMod ? { type: "gridEnd" } : { type: "rowEnd" };
        case "pageup":
            return isMod ? null : { type: "pageUp" };
        case "pagedown":
            return isMod ? null : { type: "pageDown" };
        case "enter":
            return isMod ? null : { type: "open" };
        case "space":
            return isMod
                ? { type: "selectRangeColumns" }
                : { type: "toggleRowSelection" };
        case "a":
            return isMod ? { type: "selectAll" } : null;
        case "/":
            return isMod ? null : { type: "focusSearch" };
        case "escape":
            return { type: "escape" };
        default:
            return null;
    }
};

/**
 * Whether a press matches a shortcut key like `mod+enter`, `shift+c`, or `g`.
 * Modifiers must match exactly, so `c` does not fire on Cmd+C.
 */
export const matchesShortcut = (shortcut: string, pressed: NormalizedKey) => {
    const parts = shortcut.toLowerCase().split("+");
    const key = toKeyName(parts[parts.length - 1] ?? "");
    const modifiers = new Set(parts.slice(0, -1));
    return (
        key === pressed.key &&
        modifiers.has("mod") === pressed.isMod &&
        modifiers.has("shift") === pressed.isShift &&
        modifiers.has("alt") === pressed.isAlt &&
        !pressed.hasOtherModifier
    );
};

const NATIVE_ACTIVATION_SELECTOR =
    "button, a[href], input, [role='button'], [role='checkbox'], [role='menuitem']";

/**
 * Space or Enter on a button or checkbox, which runs its own action. Only
 * controls inside the grid count: the grid may itself sit inside one (a drop
 * zone button).
 */
export const isNativeActivation = (event: {
    key: string;
    target: EventTarget | null;
    currentTarget: EventTarget | null;
}) => {
    if (
        (event.key !== " " && event.key !== "Enter") ||
        !(event.target instanceof Element) ||
        !(event.currentTarget instanceof Element)
    ) {
        return false;
    }
    const control = event.target.closest(NATIVE_ACTIVATION_SELECTOR);
    return control !== null && event.currentTarget.contains(control);
};
